/**
 * BrandLogo -- Central logo component for the platform.
 *
 * Reads branding from useBranding() hook and renders the configured logo.
 * Falls back to the bundled Opus logo (/opus-logo-branco.png) when no custom logo is set.
 *
 * Variants control sizing for different contexts:
 *   - auth: login/register pages (h-16, animate-logo-glow)
 *   - sidebar: navigation sidebars (h-7)
 *   - compact: small inline usage (h-5)
 *
 * For custom logos, the dark silhouette filter used for the white Opus logo in the light
 * theme (.brand-logo-default) is NOT applied -- custom logos are expected to be theme-neutral or provide
 * adequate contrast on both dark and light backgrounds.
 */

import { useBranding } from '@/hooks/useBranding';
import { cn } from '@/utils/cn';

export type BrandLogoVariant = 'auth' | 'sidebar' | 'compact';

interface BrandLogoProps {
  /** Size/context variant */
  variant?: BrandLogoVariant;
  /** Additional CSS classes */
  className?: string;
}

const variantStyles: Record<BrandLogoVariant, string> = {
  auth: 'h-16 animate-logo-glow',
  sidebar: 'h-7',
  compact: 'h-5',
};

const DEFAULT_LOGO = '/opus-logo-branco.png';

export function BrandLogo({ variant = 'sidebar', className }: BrandLogoProps) {
  const { platform_name, logo_url } = useBranding();

  const src = logo_url || DEFAULT_LOGO;
  const isCustomLogo = logo_url !== null && logo_url !== DEFAULT_LOGO;

  return (
    <img
      src={src}
      alt={platform_name}
      className={cn(
        variantStyles[variant],
        'object-contain',
        // Default white Opus logo: dark silhouette in the light theme (html.light .brand-logo-default,
        // globals.css). Custom logos are shown as-is in both themes.
        !isCustomLogo && 'brand-logo-default',
        className,
      )}
      draggable={false}
    />
  );
}
